import { useState } from 'react';
import { Sheet } from '@/components/sheets/Sheet';
import { Icon } from '@/components/ui/Icon';
import { NeonButton } from '@/components/ui/NeonButton';
import { CITIES } from './cities';
import { useTravelMode } from './useTravelMode';
import { useFeatureGate } from '@/modules/billing/useFeatureGate';
import { cn } from '@/lib/cn';

interface TravelSheetProps {
  open: boolean;
  onClose: () => void;
}

const DURATIONS = [
  { days: 2, label: '2 days' },
  { days: 7, label: '1 week' },
  { days: 14, label: '2 weeks' },
];

export const TravelSheet = ({ open, onClose }: TravelSheetProps) => {
  const { travel, setTravel, clearTravel } = useTravelMode();
  const gate = useFeatureGate('travel');
  const [cityKey, setCityKey] = useState<string>(travel?.city.key ?? 'sydney');
  const [days, setDays] = useState<number>(7);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApply = async () => {
    if (!gate.allowed) {
      onClose();
      gate.prompt();
      return;
    }
    setBusy(true);
    const result = await setTravel(cityKey, days);
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not set travel.');
      return;
    }
    onClose();
  };

  const handleClear = async () => {
    setBusy(true);
    await clearTravel();
    setBusy(false);
    onClose();
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      eyebrow="Travel mode"
      title={
        <>
          Set your room<span className="text-pink">.</span>
        </>
      }
      footer={
        <div className="flex items-center justify-end gap-3">
          {travel && (
            <button
              type="button"
              onClick={handleClear}
              disabled={busy}
              className="font-display text-[11px] font-bold uppercase tracking-widest text-text-muted hover:text-text-primary"
            >
              Clear
            </button>
          )}
          <NeonButton type="button" size="md" disabled={busy} onClick={handleApply} trailing={<Icon name="arrow_forward" size={14} />}>
            {busy ? 'Saving' : travel ? 'Update' : 'Apply'}
          </NeonButton>
        </div>
      }
    >
      <p className="mb-5 text-xs text-text-secondary">
        Move your map + feed to another city for a few days. The feed,
        distance pills, and Tonight Mode all re-centre.
      </p>

      <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">City</p>
      <ul className="mt-2 mb-6 divide-y divide-white/[0.06] border-y border-white/[0.06]">
        {CITIES.map((c) => {
          const selected = c.key === cityKey;
          return (
            <li key={c.key}>
              <button
                type="button"
                onClick={() => setCityKey(c.key)}
                className={cn(
                  'flex w-full items-center justify-between gap-4 py-3 text-left',
                  selected ? 'text-pink' : 'text-text-primary hover:text-pink',
                )}
              >
                <span className="font-display text-sm font-bold uppercase tracking-wide">{c.label}</span>
                <Icon name={selected ? 'radio_button_checked' : 'radio_button_unchecked'} size={16} />
              </button>
            </li>
          );
        })}
      </ul>

      <p className="font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">Duration</p>
      <div className="mt-2 flex gap-3">
        {DURATIONS.map((d) => (
          <button
            key={d.days}
            type="button"
            onClick={() => setDays(d.days)}
            className={cn(
              'border px-4 py-2 font-display text-[11px] font-bold uppercase tracking-widest',
              days === d.days ? 'border-pink text-pink' : 'border-white/[0.10] text-text-secondary hover:border-white/[0.30]',
            )}
          >
            {d.label}
          </button>
        ))}
      </div>

      {!gate.allowed && (
        <p className="mt-5 font-display text-[10px] font-bold uppercase tracking-widest text-pink">
          Travel is a premium feature — upgrade to enable.
        </p>
      )}
      {error && <p className="mt-3 font-display text-[10px] font-bold uppercase tracking-widest text-pink">— {error}</p>}
    </Sheet>
  );
};
